<script setup lang="ts">
import {
  computed, onMounted, provide, ref, watch,
} from 'vue';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import SplitButton from 'primevue/splitbutton';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import IndustryRevenueGraph from './IndustryRevenueGraph.vue';
import FileUploader from './FileUploader.vue';
import {
  useAccounts,
  useActivities,
  useAssociationMiniCards,
  useCrm,
  useEndpoint,
  useEventBus,
  useRoutingHelpers,
  useStringFunctions,
  useThemeOverrides,
  useToastNotifications,
  useUserPreferences,
  type UserPreferences,
  type CrmDetailTabName,
  EventBusEvents,
} from '@apparatix/composables';
import { useCreateActivity } from '@apparatix/composables/useCreateActivity';
import {
  type GetAccountDetailResponse, type ApiResponseBase, type SingleAccountOverview, type GetAccountAssociationResponse, type GetAccountDealsResponse, AccountAssociated,
} from '@apparatix/types/Account';
import { useAccountStore } from '@apparatix/stores/useAccountStore';
import AccountAssociationsEditor from '@apparatix/leads/AccountAssociationsEditor.vue';
import AccountDealsDocsPanel from '@apparatix/leads/AccountDealsDocsPanel.vue';
import AccountDetailsTab from '@apparatix/leads/AccountDetailsTab.vue';
import AccountNotesTab from '@apparatix/leads/AccountNotesTab.vue';
import { Section, Typography } from '@apparatix/library/core';
import { AssociationContact } from '@apparatix/types/Leads';
import ErrorFallback from '@apparatix/library/ErrorFallback.vue';
import { MenuItem } from 'primevue/menuitem';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import ActivitiesTab from './activities/ActivitiesTab.vue';
import AddActivityButton from './activities/AddActivityButton.vue';
import AccountProfiles from './AccountProfiles.vue';
import { CrmDrawerDataReadyEvent, DetailTabName, type FlyoutNavigationContext } from './types';
import { InjectEventNames } from '@apparatix/constants';
import { ActivityAddedEvent, ActivityUpdatedEvent, ActivityDeletedEvent } from './activities/commonProps';
import { Relationship } from '@apparatix/schemas/Relationship';
import { useRoute, useRouter } from 'vue-router';
import { Activity } from '@apparatix/schemas/Activities';
import MiniCardGrid from '@apparatix/leads/MiniCardGrid.vue';
import AccountLabelTag from '@apparatix/leads/AccountLabelTag.vue';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import DetailDrawerHeader from './DetailDrawerHeader.vue';
import PlusIcon from '@primeicons/vue/plus';
import PencilIcon from '@primeicons/vue/pencil';
import GlobeIcon from '@primeicons/vue/globe';
import SparklesIcon from '@primeicons/vue/sparkles';
import SitemapIcon from '@primeicons/vue/sitemap';
import BriefcaseIcon from '@primeicons/vue/briefcase';
import CalendarIcon from '@primeicons/vue/calendar';
import FolderIcon from '@primeicons/vue/folder';
import { SharedStyles } from './SharedStyles';

const props = defineProps<{ id: number, activeTab?: DetailTabName }>();

const activeTab = ref<DetailTabName>(props.activeTab || 'overview');

const store = useAccountStore();
const opportunityStore = useOpportunityStore();
const { datatable } = useThemeOverrides();
const { isEmptyString } = useStringFunctions();
const { getContactsByAdvertiser } = useAccounts();
const { handleDetailTabUpdate, isDetailTab } = useCrm();
const { openCrmDetailFlyout } = useRoutingHelpers();
const { emitEvent } = useEventBus();
const { addActivityComponent, getMobileMenuItems } = useCreateActivity();

const router = useRouter();
const route = useRoute();

const {
  activityTabSections,
  insertOrUpdateActivityInList,
  isLoading: isActivitiesLoading,
  lastTask,
  loadActivities,
  postActivity,
  removeActivityFromList,
  upcomingTask,
} = useActivities();

const onActivityAddedOrUpdated = (activity: Activity, shouldReload: boolean) => {
  if (shouldReload) {
    loadActivities(props.id, 'accounts');
  } else {
    insertOrUpdateActivityInList(activity);
  }
};

provide<ActivityAddedEvent>(InjectEventNames.crm.activities.activityCreated, onActivityAddedOrUpdated);
provide<ActivityUpdatedEvent>(InjectEventNames.crm.activities.activityUpdated, onActivityAddedOrUpdated);
provide<ActivityDeletedEvent>(InjectEventNames.crm.activities.activityDeleted, removeActivityFromList);

const miniCardAccounts = ref<AccountAssociated[]>([]);
const miniCardContacts = ref<AssociationContact[]>([]);

const loadingErrors = ref({
  activities: false,
  associations: false,
  details: false,
  locations: false,
  overview: false,
  revenue: false,
  deals: false,
});

const activityParentObject = computed<Relationship>(() => ({ id: props.id, name: store.accountDetails?.name, type: 'Account' }));

const { showWarning } = useToastNotifications();

const preferences = ref<UserPreferences>();
const { getPreferences } = useUserPreferences();

const {
  account: miniCardAccount,
  accountsLoading: accountMiniCardLoading,
  contact: miniCardContact,
  contactsLoading: contactMiniCardLoading,
  shouldShowAccount: showAccountMiniCard,
  shouldShowContact: showContactMiniCard,
} = useAssociationMiniCards(miniCardAccounts, miniCardContacts);

const {
  isLoading: isDetailLoading, makeRequest: makeDetailRequest, setEndpointUrl: detailRequestEndpoint,
} = useEndpoint<ApiResponseBase<GetAccountDetailResponse>>({
  type: 'GET',
  successField: 'data',
  startInLoadingState: true,
  onSuccess: response => {
    const { account } = response.data;
    store.accountDetails = account;
    store.options = response.data.options;
    store.invoiceFieldsLocked = response.data.permissions.some((permission) => permission.tag === 'invoiceFieldsLocked');
    store.accountOwnerLocked = response.data.permissions.some((permission) => permission.tag === 'accountOwnerLocked');
    store.canUseCreateButton = response.data.permissions.some((permission) => permission.tag === 'canUseCreateButton');
    store.createProposalFromAccount = response.data.permissions.some((permission) => permission.tag === 'createProposalFromAccount');

    emitEvent<CrmDrawerDataReadyEvent>(EventBusEvents.CrmDetailLoaded, {
      title: account.name,
      type: 'Account',
    });
  },
  onError: () => {
    loadingErrors.value.details = true;
  },
  toast: { showOnSuccess: false },
});

const {
  isLoading: isOverviewLoading, makeRequest: makeOverviewRequest, setEndpointUrl: overviewRequestEndpoint,
} = useEndpoint<ApiResponseBase<SingleAccountOverview>>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    store.accountOverview = response.data.overview;
    store.revenueGraph = response.data.revenueGraph;
    store.currentLocations = response.data.currentLocations;
    store.futureLocations = response.data.futureLocations;
    loadingErrors.value = {
      ...loadingErrors.value,
      overview: false,
      revenue: false,
      locations: false,
    };
  },
  onError: () => {
    loadingErrors.value = {
      ...loadingErrors.value,
      overview: true,
      revenue: true,
      locations: true,
    };
  },
  toast: { showOnSuccess: false },
});

const {
  isLoading: isAssociationsLoading, makeRequest: makeAssociationsRequest, setEndpointUrl: makeAssociationsEndpoint,
} = useEndpoint<ApiResponseBase<GetAccountAssociationResponse>>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    store.accountAssociations = response.data;
    miniCardAccounts.value = store.accountAssociations.accounts;
    miniCardContacts.value = store.accountAssociations.contacts;
    store.hasLinkAccountButton = response.data.permissions.some((permission) => permission.tag === 'hasLinkAccountButton');
    accountMiniCardLoading.value = false;
    contactMiniCardLoading.value = false;
    loadingErrors.value.associations = false;
  },
  onError: () => {
    loadingErrors.value.associations = true;
  },
  toast: { showOnSuccess: false },
});

const {
  isLoading: isDealsAndDocsLoading, makeRequest: makeDealsAndDocsRequest, setEndpointUrl: makeDealsAndDocsEndpoint,
} = useEndpoint<ApiResponseBase<GetAccountDealsResponse>>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    store.accountDeals = response.data;
    loadingErrors.value.deals = false;
  },
  onError: () => {
    loadingErrors.value.deals = true;
  },
  toast: { showOnSuccess: false },
});

const campaignId = ref(0);

const { makeRequest: makeCreateAutoOpportunityRequest } = useEndpoint({
  type: 'POST',
  url: '/crm/opportunities',
  successField: 'data',
  onSuccess: response => {
    opportunityStore.addOpportunity(response.data.opportunity, true);
    campaignId.value = response.data.opportunity.id;
  },
  toast: { showOnSuccess: false },
});

const displayButton = computed(() => {
  if (!preferences.value) {
    return false;
  }
  // Check if User Preference Enabled
  if (!preferences.value.crmCreateOpportunityFromAccount) {
    return false;
  }
  // Check if Account falls into one of the following type categories
  // @TODO: 'agency' to be added in future release
  const required = ['advertiser', 'prospective', 'previous'];
  const lowerTypes = new Set(
    store.accountDetails.rawTypes.map(type => type.toLowerCase()),
  );
  return required.some(item => lowerTypes.has(item));
});

onMounted(async () => {
  const userPrefs = await getPreferences();
  if (userPrefs) {
    preferences.value = userPrefs;
  }

  loadActivities(activityParentObject.value.id, 'accounts');
});

watch(
  () => props.id,
  (newAccountId) => {
    if (newAccountId) {
      accountMiniCardLoading.value = true;
      contactMiniCardLoading.value = true;
      detailRequestEndpoint(`/crm/accounts/${props.id}`);
      overviewRequestEndpoint(`/crm/accounts/${props.id}/overview`);
      makeAssociationsEndpoint(`/crm/accounts/${props.id}/associations`);
      makeDealsAndDocsEndpoint(`/crm/accounts/${props.id}/deals`);

      makeDetailRequest();
      makeOverviewRequest();
      makeAssociationsRequest();
      makeDealsAndDocsRequest();
    }
  },
  { immediate: true },
);

watch(() => route.path, newPath => {
  if (!newPath) return;

  const segments = newPath.split('/');
  const tab = segments.pop();

  if (isDetailTab(tab)) {
    activeTab.value = <DetailTabName>tab;
  }
});

const createOpportunity = async () => {
  if (isMissingPrimaryContact()) {
    toastMissingPrimaryContactMessage();
    return;
  }

  const agencyAccount = store.accountAssociations.accounts.find(account => account.c2RawType === 'Agency');

  const contacts = [];

  if (agencyAccount) {
    await getContactsByAdvertiser(agencyAccount.c2Id, results => {
      if (results?.length) {
        results.forEach(result => {
          contacts.push({
            id: result.id,
            labelIds: result.contactLabels.map(lbl => lbl.contactLabelId),
          });
        });
      }
    });
  }

  const mappedContacts = store.accountAssociations.contacts.map(contact => ({
    id: contact.id,
    labelIds: contact.contactLabels.map(lbl => lbl.contactLabelId).filter(id => id !== 1 || !agencyAccount),
  }));

  const combinedContacts = [...contacts];

  mappedContacts.forEach(result => {
    if (!combinedContacts.some(existing => existing.id === result.id)) {
      combinedContacts.push(result);
    }
  });

  const payload = {
    data: {
      opportunity: {
        type: 'Opportunity',
        accountId: props.id,
        contacts: combinedContacts,
      },
    },
  };

  await makeCreateAutoOpportunityRequest(payload);

  if (store.useCrmCampaigns) {
    openCrmDetailFlyout('accounts', 'opportunity', campaignId.value);
  }
};

const createProposal = async () => {
  if (isMissingPrimaryContact()) {
    toastMissingPrimaryContactMessage();
    return;
  }
  // Opportunity Automation
  await createOpportunity();
  // open solution center associated to newly created campaign
  const url = `/apx/proposalTool/index-continue.html?advertiser=${props.id.toString()}&cn=${campaignId.value.toString()}&pid=`;
  window.open(url, '_blank');
};

const isMissingPrimaryContact = () => isEmptyString(store.accountDetails.primaryContact);

const toastMissingPrimaryContactMessage = () => {
  showWarning('Please add a Primary Contact to Account');
};

const splitButtonItems = ref<MenuItem[]>([
  {
    label: 'Create Opportunity',
    icon: PlusIcon,
    command: () => createOpportunity(),
    class: 'pv-menu-item-primary-text',
  },
]);

const mobileMenuItems = computed<MenuItem[]>(() => {
  const baseItems: MenuItem[] = getMobileMenuItems(['Note', 'Reminder', 'Task'], 'accounts', { name: store.accountDetails?.name, id: store.accountDetails?.id, type: 'Account' });
  const conditionalItems: MenuItem[] = [];

  if (store.useCrmCampaigns) {
    if (displayButton.value && !store.createProposalFromAccount) {
      conditionalItems.push({ separator: true });
      conditionalItems.push({
        label: 'Create Opportunity',
        icon: PencilIcon,
        disabled: !store.canUseCreateButton,
        command: () => createOpportunity(),
      });
    }

    if (displayButton.value && store.createProposalFromAccount) {
      conditionalItems.push({
        label: 'Proposals',
        items: splitButtonItems.value,
      });
    }
  }

  if (displayButton.value && !store.useCrmCampaigns) {
    conditionalItems.push({ separator: true });
    conditionalItems.push({
      label: 'Create Proposal',
      icon: PencilIcon,
      disabled: !store.canUseCreateButton,
      command: () => createProposal(),
    });
  }

  return [...baseItems, ...conditionalItems];
});

const handleNavigateForward = (context: FlyoutNavigationContext) => {
  const { basePath } = route.meta;
  const path = `${basePath}/detail/${context.view.toLowerCase()}/${context.id}`;
  router.push({ path });
};

const postActivityForward = (payload) => {
  postActivity(payload.typeId, payload.modelObject.id, payload.modelObject.name, payload.modelObject.view);
};
</script>
<template>
  <DetailDrawerHeader
    v-if="!isDetailLoading"
    type="Account"
    :menu-items="mobileMenuItems"
    :title="store.accountDetails.name"
    :subtitle="store.accountDetails.formattedType"
  >
    <template #subtitle-items>
      <AccountLabelTag :label="store.accountDetails.label" />
    </template>
    <template #buttons>
      <AddActivityButton
        view="accounts"
        :button-pt-props="{severity: displayButton ? 'secondary' : undefined}"
        :parent-object="activityParentObject"
      />
      <template v-if="store.useCrmCampaigns">
        <Button
          v-if="displayButton && !store.createProposalFromAccount"
          :disabled="!store.canUseCreateButton"
          @click="createOpportunity()"
        >
          <PencilIcon />
          Create Opportunity
        </Button>
        <SplitButton
          v-if="displayButton && store.createProposalFromAccount"
          :model="splitButtonItems"
          label="Create Proposal"
          :disabled="!store.canUseCreateButton"
          @click="createProposal()"
        >
          <template #icon>
            <PencilIcon />
          </template>
        </SplitButton>
      </template>
      <Button
        v-if="displayButton && !store.useCrmCampaigns"
        :disabled="!store.canUseCreateButton"
        @click="createProposal()"
      >
        <PencilIcon />
        Create Proposal
      </Button>
    </template>
  </DetailDrawerHeader>
  <Tabs
    v-if="!isDetailLoading"
    class="grow"
    :value="activeTab"
    @update:value="(val: CrmDetailTabName) => handleDetailTabUpdate(val, () => activeTab = val)"
  >
    <TabList class="overflow-x-auto whitespace-nowrap scrolling-touch">
      <Tab value="overview" :class="SharedStyles.flyouts.tabClasses">
        <GlobeIcon />
        Overview
      </Tab>
      <Tab value="profile" :class="SharedStyles.flyouts.tabClasses">
        <SparklesIcon />
        Profile
      </Tab>
      <Tab value="relationships" :class="SharedStyles.flyouts.tabClasses">
        <SitemapIcon />
        Relationships
      </Tab>
      <Tab value="deals" :class="SharedStyles.flyouts.tabClasses">
        <BriefcaseIcon />
        Deals &amp; Docs
      </Tab>
      <Tab value="activity" :class="SharedStyles.flyouts.tabClasses">
        <CalendarIcon />
        Activity
      </Tab>
      <Tab value="files" :class="SharedStyles.flyouts.tabClasses">
        <FolderIcon />
        Files
      </Tab>
    </TabList>
    <TabPanels class="!bg-[var(--neutral-background)] grow">
      <div class="w-full grid grid-cols-3 grid-flow-row auto-rows-auto lg:gap-x-9 gap-y-4 p-2 lg:p-9">
        <div class="flex flex-col gap-4 col-span-3">
          <TabPanel value="overview">
            <div class="flex flex-col gap-6">
              <ErrorFallback :loading-indicator="isAssociationsLoading" :did-error="loadingErrors.associations">
                <MiniCardGrid
                  :show-account-mini-card="showAccountMiniCard"
                  :show-contact-mini-card="showContactMiniCard"
                  :mini-card-account="miniCardAccount"
                  :mini-card-contact="miniCardContact"
                  :account-mini-card-loading="accountMiniCardLoading"
                  :contact-mini-card-loading="contactMiniCardLoading"
                  :last-task="lastTask"
                  :upcoming-task="upcomingTask"
                  :is-loading="isActivitiesLoading"
                  @post-activity="postActivityForward"
                />
              </ErrorFallback>

              <ErrorFallback :loading-indicator="isDetailLoading" :did-error="loadingErrors.details">
                <AccountDetailsTab />
                <AccountNotesTab />
              </ErrorFallback>

              <ErrorFallback :loading-indicator="isOverviewLoading" :did-error="loadingErrors.overview">
                <Section class="mb-3" :footer="false">
                  <template #title>
                    <Typography variant="section-heading">
                      Revenue Graph
                    </Typography>
                  </template>
                  <IndustryRevenueGraph />
                </Section>
                <Section class="mb-3" :footer="false">
                  <DataTable
                    :value="store.currentLocations"
                    table-style="min-width: 50rem;"
                    class="no-left-padding"
                    :dt="datatable.noLeftPadding"
                  >
                    <template #header>
                      <Typography variant="section-heading">
                        Current Locations
                        <Badge :value="store.currentLocations.length" severity="secondary" />
                      </Typography>
                    </template>
                    <template #empty>
                      No current board locations for this advertiser.
                    </template>
                    <template v-if="store.currentLocations.length > 0">
                      <Column field="face" header="Face" />
                      <Column field="class" header="Contract Class" />
                      <Column field="venue" header="Product Type" />
                      <Column field="market" header="Market" />
                      <Column field="description" header="Description" />
                      <Column field="postingStart" header="Posting Start" />
                      <Column field="postingEnd" header="Posting End" />
                      <Column field="creative" header="Creative" />
                    </template>
                  </DataTable>
                </Section>
                <Section class="mb-3" :footer="false">
                  <DataTable
                    :value="store.futureLocations"
                    table-style="min-width: 50rem;"
                    class="no-left-padding"
                    :dt="datatable.noLeftPadding"
                  >
                    <template #header>
                      <Typography variant="section-heading">
                        Upcoming Locations
                        <Badge :value="store.futureLocations.length" severity="secondary" />
                      </Typography>
                    </template>
                    <template #empty>
                      No upcoming board locations for this advertiser.
                    </template>
                    <template v-if="store.futureLocations.length > 0">
                      <Column field="face" header="Face" />
                      <Column field="class" header="Contract Class" />
                      <Column field="venue" header="Product Type" />
                      <Column field="market" header="Market" />
                      <Column field="description" header="Description" />
                      <Column field="postingStart" header="Posting Start" />
                      <Column field="postingEnd" header="Posting End" />
                      <Column field="creative" header="Creative" />
                    </template>
                  </DataTable>
                </Section>
              </ErrorFallback>
            </div>
          </TabPanel>
          <TabPanel value="profile">
            <AccountProfiles :account-id="Number(props.id)" />
          </TabPanel>
          <TabPanel value="relationships">
            <div class="flex flex-col">
              <ErrorFallback :loading-indicator="isAssociationsLoading" :did-error="loadingErrors.associations">
                <AccountAssociationsEditor @navigate-forward="handleNavigateForward" @post-activity="postActivityForward" />
              </ErrorFallback>
            </div>
          </TabPanel>
          <TabPanel value="deals">
            <div class="flex flex-col">
              <FormSkeleton v-if="isDealsAndDocsLoading" />
              <ErrorFallback v-else :loading-indicator="isDealsAndDocsLoading" :did-error="loadingErrors.deals">
                <AccountDealsDocsPanel @navigate-forward="handleNavigateForward" />
              </ErrorFallback>
            </div>
          </TabPanel>
          <TabPanel value="activity">
            <ActivitiesTab
              v-if="store?.accountDetails"
              view="accounts"
              :parent-object="activityParentObject"
              :sections="activityTabSections"
              :is-loading="isActivitiesLoading"
            />
          </TabPanel>
          <TabPanel value="files">
            <div class="flex flex-col">
              <FileUploader :id="props.id" endpoint="accounts" />
            </div>
          </TabPanel>
        </div>
      </div>
    </TabPanels>
  </Tabs>
  <component :is="addActivityComponent" />
</template>
